import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Text } from '@/ui';
import useFormatNumber from '@/hooks/useFormatNumber';
import { formatCurrency } from '@/utils/currency';
import { DataTable } from '@/components/data-table';
import type { CardField } from '@/components/data-table';
import { cn } from '@/lib/utils';

type ResidualData = {
  _id: string;
  companyName: string;
  transactionCount: number;
  paymentVolume: number;
  totalFee: number;
  finalFee: number;
  residualEarned: number;
};

type Props = {
  residuals: ResidualData[];
};

function ResidualsTable({ residuals }: Props) {
  const { t } = useTranslation();
  const formatValue = useFormatNumber();

  const totals = useMemo(
    () =>
      residuals.reduce(
        (acc, residual) => ({
          transactionCount: acc.transactionCount + residual.transactionCount,
          paymentVolume: acc.paymentVolume + residual.paymentVolume,
          totalFee: acc.totalFee + residual.totalFee,
          finalFee: acc.finalFee + residual.finalFee,
          residualEarned: acc.residualEarned + residual.residualEarned,
        }),
        {
          transactionCount: 0,
          paymentVolume: 0,
          totalFee: 0,
          finalFee: 0,
          residualEarned: 0,
        },
      ),
    [residuals],
  );

  const columnHelper = createColumnHelper<ResidualData>();
  const mobileCardFields = useMemo<CardField<ResidualData>[]>(
    () => [
      { key: 'companyName', primary: true, render: (row) => row.original.companyName },
      {
        key: 'transactionCount',
        label: t('Residuals.transaction_count'),
        render: (row) => formatValue(row.original.transactionCount, false, 0),
      },
      {
        key: 'paymentVolume',
        label: t('Residuals.payment_volume'),
        render: (row) => formatCurrency(row.original.paymentVolume, 'USD'),
      },
      {
        key: 'totalFee',
        label: t('Residuals.total_fee'),
        render: (row) => formatCurrency(row.original.totalFee, 'USD'),
      },
      {
        key: 'finalFee',
        label: t('Residuals.final_fee'),
        render: (row) => formatCurrency(row.original.finalFee, 'USD'),
      },
      {
        key: 'residualEarned',
        label: t('Residuals.residual_earned'),
        render: (row) => formatCurrency(row.original.residualEarned, 'USD'),
      },
    ],
    [t, formatValue],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('companyName', {
        id: 'companyName',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.company')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {getValue()}
          </Text>
        ),
        size: 200,
      }),
      columnHelper.accessor('transactionCount', {
        id: 'transactionCount',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.transaction_count')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {formatValue(getValue(), false, 0)}
          </Text>
        ),
        size: 120,
      }),
      columnHelper.accessor('paymentVolume', {
        id: 'paymentVolume',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.payment_volume')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {formatCurrency(getValue(), 'USD')}
          </Text>
        ),
        size: 140,
      }),
      columnHelper.accessor('totalFee', {
        id: 'totalFee',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.total_fee')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {formatCurrency(getValue(), 'USD')}
          </Text>
        ),
        size: 120,
      }),
      columnHelper.accessor('finalFee', {
        id: 'finalFee',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.final_fee')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {formatCurrency(getValue(), 'USD')}
          </Text>
        ),
        size: 120,
      }),
      columnHelper.accessor('residualEarned', {
        id: 'residualEarned',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Residuals.residual_earned')}
          </Text>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {formatCurrency(getValue(), 'USD')}
          </Text>
        ),
        size: 120,
      }),
    ],
    [t, formatValue],
  );

  const table = useReactTable({
    data: residuals,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  /** Match DataTable column sizing so totals align (same ratios as TanStack `size`). */
  const totalsGridTemplate = table
    .getVisibleLeafColumns()
    .map((col) => `minmax(0,${col.getSize()}fr)`)
    .join(' ');

  return (
    <div className='space-y-0'>
      <DataTable table={table} mobileCardFields={mobileCardFields} />
      {/* Totals row - same layout as table */}
      <div
        className={cn(
          'grid w-full gap-0 rounded-b-md border border-t-0 border-border',
          'bg-muted/50 font-medium text-sm',
        )}
        style={{ gridTemplateColumns: totalsGridTemplate }}
      >
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {t('Residuals.total')}
          </Text>
        </span>
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {formatValue(totals.transactionCount, false, 0)}
          </Text>
        </span>
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {formatCurrency(totals.paymentVolume, 'USD')}
          </Text>
        </span>
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {formatCurrency(totals.totalFee, 'USD')}
          </Text>
        </span>
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {formatCurrency(totals.finalFee, 'USD')}
          </Text>
        </span>
        <span className='min-w-0 px-4 pt-1 pb-2'>
          <Text size='xs' weight='light-medium'>
            {formatCurrency(totals.residualEarned, 'USD')}
          </Text>
        </span>
      </div>
    </div>
  );
}

export default ResidualsTable;
